import React, { useState, useEffect, useRef } from 'react';
import { BarChart3, Users, Clock, TrendingUp, Maximize2, Move, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import EnhancedVideoHeatmap from './EnhancedVideoHeatmap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Stats Card
const StatsCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => {
  const colors = {
    blue: { bg: '#3b82f620', icon: '#3b82f6' },
    green: { bg: '#22c55e20', icon: '#22c55e' },
    purple: { bg: '#a855f720', icon: '#a855f7' },
    orange: { bg: '#f59e0b20', icon: '#f59e0b' }
  };
  const c = colors[color] || colors.blue;
  
  return (
    <div style={{
      background: '#f8f9fa',
      borderRadius: 10,
      padding: 14,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      border: '1px solid #e9ecef'
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: c.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon size={18} color={c.icon} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#212529' }}>{value}</div>
        <div style={{ fontSize: 12, color: '#6c757d' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
  );
};

// Views Chart
const ViewsChart = ({ data }) => {
  const maxCount = Math.max(...(data?.map(d => d.count) || [1]), 1);
  
  if (!data?.length) return null;
  
  return (
    <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1px solid #e9ecef' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
        Просмотры по дням
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
        {data?.slice(-14).map((day, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div 
              style={{
                width: '100%',
                height: Math.max((day.count / maxCount) * 60, 3),
                background: '#3b82f6',
                borderRadius: '3px 3px 0 0'
              }}
              title={`${day.date}: ${day.count}`}
            />
            <div style={{ fontSize: 8, color: '#adb5bd', marginTop: 3 }}>
              {day.date?.slice(8)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Device Stats
const DeviceStats = ({ data }) => {
  const total = Object.values(data || {}).reduce((a, b) => a + b, 0) || 1;
  const colors = { desktop: '#3b82f6', mobile: '#22c55e', tablet: '#a855f7', unknown: '#6b7280' };
  const labels = { desktop: 'ПК', mobile: 'Моб.', tablet: 'Планшет', unknown: '?' };
  
  if (!data || Object.keys(data).length === 0) return null;
  
  return (
    <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1px solid #e9ecef' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
        Устройства
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {Object.entries(data).map(([device, count]) => (
          <div key={device} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: `${(count / total) * 100}%`,
              minWidth: 30,
              height: 20,
              background: colors[device] || '#6b7280',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 6,
              fontSize: 11,
              color: '#fff'
            }}>
              {labels[device] || device}
            </div>
            <span style={{ fontSize: 11, color: '#6c757d' }}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Heatmap
const VideoHeatmap = ({ data, duration }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !data?.length) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.fillStyle = '#f1f3f4';
    ctx.fillRect(0, 0, width, height);
    
    const maxCount = Math.max(...data.map(d => d.count), 1);
    
    data.forEach(point => {
      const intensity = point.count / maxCount;
      const x = (point.second / Math.max(duration, 1)) * width;
      const barHeight = intensity * (height - 15);
      
      const r = Math.min(255, Math.floor(intensity * 2 * 255));
      const g = Math.min(255, Math.floor((1 - intensity) * 2 * 255));
      ctx.fillStyle = `rgb(${r}, ${g}, 100)`;
      ctx.fillRect(x, height - 15 - barHeight, Math.max(width / duration, 2), barHeight);
    });
    
    ctx.fillStyle = '#dee2e6';
    ctx.fillRect(0, height - 3, width, 3);
    
  }, [data, duration]);
  
  if (!data?.length) return null;
  
  return (
    <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1px solid #e9ecef' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
        Тепловая карта
      </div>
      <canvas 
        ref={canvasRef} 
        width={400} 
        height={60} 
        style={{ width: '100%', height: 60, borderRadius: 6 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: '#adb5bd' }}>
        <span>0:00</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

// Timecode Table
const TimecodeTable = ({ timecodes, panoramas }) => {
  if (!timecodes?.length) return null;
  
  return (
    <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1px solid #e9ecef' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
        Таймкоды
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {timecodes.slice(0, 5).map((tc, i) => {
          const pano = panoramas?.find(p => p.id === tc.panorama_id);
          const thumbnailUrl = pano ? `${API}/panoramas/${pano.id}/preview` : null;
          
          return (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 8,
              background: '#fff',
              borderRadius: 6,
              border: '1px solid #e9ecef'
            }}>
              {/* Thumbnail */}
              <div style={{
                width: 56,
                height: 32,
                borderRadius: 4,
                overflow: 'hidden',
                flexShrink: 0,
                background: '#e9ecef'
              }}>
                {thumbnailUrl ? (
                  <img 
                    src={thumbnailUrl} 
                    alt={pano?.name || 'Панорама'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#adb5bd',
                    fontSize: 8
                  }}>
                    —
                  </div>
                )}
              </div>
              
              {/* Time badge */}
              <div style={{
                background: '#d1fae5',
                color: '#059669',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600
              }}>
                {formatTime(tc.timecode)}
              </div>
              
              {/* Stats */}
              <div style={{ flex: 1, fontSize: 11, color: '#6c757d' }}>
                {tc.trigger_count} сраб.
              </div>
              
              {/* Return */}
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: tc.return_to_video_rate > 50 ? '#22c55e' : '#f59e0b' 
              }}>
                {tc.return_to_video_rate}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Interaction Stats
const InteractionStats = ({ stats }) => {
  if (!stats) return null;
  
  const hasData = stats.fullscreen_enters > 0 || stats.miniplayer_drags > 0 || 
                  stats.miniplayer_shows > 0 || stats.countdown?.total_countdowns > 0;
  
  if (!hasData) return null;
  
  return (
    <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1px solid #e9ecef' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
        Взаимодействия
      </div>
      
      {stats.countdown?.total_countdowns > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#22c55e' }}>{stats.countdown.clicks}</div>
            <div style={{ fontSize: 10, color: '#6c757d' }}>Клик сразу</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>{stats.countdown.waits}</div>
            <div style={{ fontSize: 10, color: '#6c757d' }}>Дождались</div>
          </div>
        </div>
      )}
      
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {stats.fullscreen_enters > 0 && (
          <div style={{ textAlign: 'center', padding: 6, background: '#fff', borderRadius: 6, minWidth: 60 }}>
            <Maximize2 size={14} color="#3b82f6" />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>{stats.fullscreen_enters}</div>
            <div style={{ fontSize: 9, color: '#6c757d' }}>Fullscreen</div>
          </div>
        )}
        {stats.miniplayer_drags > 0 && (
          <div style={{ textAlign: 'center', padding: 6, background: '#fff', borderRadius: 6, minWidth: 60 }}>
            <Move size={14} color="#22c55e" />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>{stats.miniplayer_drags}</div>
            <div style={{ fontSize: 9, color: '#6c757d' }}>Drag</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const GameVideoAnalytics = ({ gameId, getAuthHeader, videoDuration = 300, panoramas = [] }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState(365);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/analytics/video/stats/${gameId}?days=${days}`, {
          headers: getAuthHeader()
        });
        setStats(response.data);
      } catch (error) {
        console.debug('Analytics not available:', error);
      } finally {
        setLoading(false);
      }
    };

    if (gameId) {
      fetchStats();
    }
  }, [gameId, days, getAuthHeader]);

  if (loading) {
    return (
      <div style={{
        background: '#f8f9fa',
        borderRadius: 10,
        padding: 16,
        marginTop: 16,
        marginBottom: 16,
        border: '1px solid #e9ecef',
        color: '#6c757d',
        fontSize: 13
      }}>
        Загрузка аналитики...
      </div>
    );
  }

  if (!stats || !stats.video_stats || stats.video_stats.total_views === 0) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 12,
        marginTop: 16,
        marginBottom: 16,
        border: '1px solid #e9ecef',
        padding: '16px 18px',
        color: '#475569',
        fontSize: 13,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937', marginBottom: 4 }}>
              Нет данных за выбранный период
            </div>
            <div style={{ color: '#6b7280' }}>
              Попробуйте увеличить интервал — события могли быть записаны раньше.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[7, 30, 90, 365].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid ' + (days === d ? '#3b82f6' : '#d1d5db'),
                  background: days === d ? '#3b82f6' : 'white',
                  color: days === d ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {d === 365 ? '1 год' : `${d} дн.`}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      marginTop: 16,
      marginBottom: 16,
      border: '1px solid #e9ecef',
      overflow: 'hidden'
    }}>
      {/* Header - always visible */}
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          background: '#f8f9fa',
          borderBottom: expanded ? '1px solid #e9ecef' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BarChart3 size={18} color="#3b82f6" />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>
            Аналитика видео
          </span>
          <span style={{ 
            fontSize: 12, 
            color: '#6c757d',
            background: '#e9ecef',
            padding: '2px 8px',
            borderRadius: 10
          }}>
            {stats.video_stats.total_views} просмотров
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {expanded && (
            <select
              value={days}
              onChange={(e) => { e.stopPropagation(); setDays(Number(e.target.value)); }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: 6,
                padding: '4px 8px',
                color: '#212529',
                fontSize: 12
              }}
            >
              <option value={7}>7 дней</option>
              <option value={30}>30 дней</option>
              <option value={90}>90 дней</option>
            </select>
          )}
          {expanded ? <ChevronUp size={18} color="#6c757d" /> : <ChevronDown size={18} color="#6c757d" />}
        </div>
      </div>

      {/* Expandable content */}
      {expanded && (
        <div style={{ padding: 16 }}>
          {/* Main stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: 10,
            marginBottom: 16
          }}>
            <StatsCard 
              icon={Eye} 
              title="Просмотров" 
              value={stats.video_stats.total_views}
              color="blue"
            />
            <StatsCard 
              icon={Users} 
              title="Зрителей" 
              value={stats.video_stats.unique_viewers}
              color="green"
            />
            <StatsCard 
              icon={Clock} 
              title="Ср. время" 
              value={formatTime(stats.video_stats.avg_duration_watched || 0)}
              color="purple"
            />
            <StatsCard 
              icon={TrendingUp} 
              title="Досмотр" 
              value={`${Math.round(stats.video_stats.avg_completion_percent || 0)}%`}
              color="orange"
            />
          </div>

          {/* Charts row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 12,
            marginBottom: 12
          }}>
            <ViewsChart data={stats.video_stats.views_by_day} />
            <DeviceStats data={stats.video_stats.views_by_device} />
          </div>

          {/* Enhanced Heatmap */}
          <div style={{ marginBottom: 12 }}>
            <EnhancedVideoHeatmap 
              gameId={gameId}
              stats={stats}
              duration={videoDuration}
              timecodes={stats.timecode_stats?.timecodes || []}
              compact={true}
            />
          </div>

          {/* Bottom row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 12
          }}>
            <TimecodeTable timecodes={stats.timecode_stats?.timecodes} panoramas={panoramas} />
            <InteractionStats stats={stats.interaction_stats} />
          </div>
        </div>
      )}
    </div>
  );
};

export default GameVideoAnalytics;
